'use strict';
import {getLink} from '../../core-utilities/domain/ReduxUtils';
import * as TASK_TYPE from '../enums/TASK_TYPE';
import {TASK_CLASS_NAME, TASK_COLLECTION} from './config';


export const CLASS_NAME = TASK_CLASS_NAME;
export const COLLECTION = TASK_COLLECTION;

export function TaskDomainProvider() {
  return {
    $get: [
      '$injector',
      'AuthService',
      'FirebaseConnectionService',
      'AbstractDomainClass',
      '$$AbstractTask',
      'restUtils',
      'FirebaseQueueService',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        AuthService,
        FirebaseConnectionService,
        AbstractDomainClass,
        $$AbstractTask,
        restUtils,
        FirebaseQueueService,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        /**
         * @param {Object} data Serialized data
         * @class
         * @implements {HasMembersDomainClass}
         */
        function TaskDomain(data) {
          AbstractDomainClass.call(this, TaskDomain, data);
        }

        $$AbstractTask.__inheritFrom(TaskDomain, CLASS_NAME, COLLECTION);

        TaskDomain.$constraints.addConstraints({
          assetSpecific: {type: 'boolean', defaultValue: false},

          members: {type: 'DomainCollection', domainClass: 'UserDomain', links: 'private-tasks'},
          membersPersonnel: {type: 'DomainCollection', domainClass: 'PersonDomain', links: 'private-tasks'},
          membersTeams: {type: 'DomainCollection', domainClass: 'TeamDomain', links: 'private-tasks'},

          assetTasks: {type: 'DomainCollection', domainClass: 'AssetTaskDomain', link: 'task'},
          formRequirements: {
            type: 'DomainCollection',
            domainClass: 'FormRequirementDomain',
            link: 'task',
            belongsTo: true
          },
          fileRequirements: {
            type: 'DomainCollection',
            domainClass: 'FileRequirementDomain',
            link: 'task',
            belongsTo: true
          }
        });

        // for fallback API methods
        TaskDomain.fallbackIdProperty = 'taskId';
        TaskDomain.createFallbackMethod = 'tasks/create';
        TaskDomain.updateFallbackMethod = 'tasks/update';
        TaskDomain.updateStatusFallbackMethod = 'tasks/status/update';

        // more methods go here;

        TaskDomain.filterByTaskType = function filterByTaskType(taskType) {
          if (taskType === TASK_TYPE.PROJECT_SITE) {
            return tasks => tasks.filter(task => task && task.assetSpecific);
          } else {
            return tasks => tasks.filter(task => task && !task.assetSpecific);
          }
        };

        TaskDomain.propagateTaskToAssets = function propagateTaskToAssets(task) {
          const request = {
            method: 'project/assets/tasks/propagate',
            orgId: task.org,
            projectId: getLink(task, 'project').id,
            taskId: task.$id
          };

          // TODO(migration): {timeout: 5*60*1000}
          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC)
            .then(function (it) {
              //console.log('TaskDomain: added task to all assets.');
              return it;
            }, function (error) {
              console.warn('TaskDomain: failed to add task to all assets.');
              console.error(error);
              return Promise.reject(error);
            });
        };

        TaskDomain.prototype.create = restUtils.createProto(TaskDomain, {
          getBody: (instance, options) => ({
            name: instance.name,
            description: instance.description,
            isPrivate: instance.isPrivate,
            scheduled: instance.scheduled ? instance.scheduled.getTime() : null,
            useTime: instance.useTime,
            duration: instance.duration,
            assignedTo: instance.assignedTo ? getLink(instance, 'assignedTo') : null,
            milestoneId: instance.milestone ? instance.milestone.$id : null,
            projectId: instance.project && instance.project.$id,
            orgId: instance.project && getLink(instance.project, 'organization').id,
            assetSpecific: instance.assetSpecific,
            templateOptions: options || null,
          }),
        });

        TaskDomain.prototype.update = restUtils.updateProto(TaskDomain);

        TaskDomain.update = restUtils.updateStatic(TASK_COLLECTION/*, {
          afterSuccess,
        }*/);

        TaskDomain.changePersonRole = changePersonRoleStatic;
        TaskDomain.prototype.changePersonRole = changePersonRoleProto;
        TaskDomain.changeTeamRole = changeTeamRoleStatic;
        TaskDomain.prototype.changeTeamRole = changeTeamRoleProto;
        TaskDomain.removePersonRole = removePersonRoleStatic;
        TaskDomain.prototype.removePersonRole = removePersonRoleProto;
        TaskDomain.removeTeamRole = removeTeamRoleStatic;
        TaskDomain.prototype.removeTeamRole = removeTeamRoleProto;
        TaskDomain.prototype.toDescriptiveString = toDescriptiveStringProto;

        function changePersonRoleProto(personId, role) {
          return changePersonRoleStatic(this.$id, personId, role);
        }

        function changePersonRoleStatic(id, personId, role) {
          const request = {
            method: 'tasks/members/change-person',
            task: id,
            targetProfile: personId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function changeTeamRoleProto(teamId, role) {
          return changeTeamRoleStatic(this.$id, teamId, role);
        }

        function changeTeamRoleStatic(id, teamId, role) {
          const request = {
            method: 'tasks/members/change-team',
            task: id,
            targetTeam: teamId,
            role: role
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removePersonRoleProto(personId) {
          return removePersonRoleStatic(this.$id, personId);
        }

        function removePersonRoleStatic(id, personId) {
          const request = {
            method: 'tasks/members/remove-person',
            task: id,
            targetProfile: personId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removeTeamRoleProto(teamId) {
          return removeTeamRoleStatic(this.$id, teamId);
        }

        function removeTeamRoleStatic(id, teamId) {
          const request = {
            method: 'tasks/members/remove-team',
            task: this.$id,
            targetTeam: teamId
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function toDescriptiveStringProto() {
          return `${this} - ${this.project}`
        }

        function afterSuccess(instance) {
          // TODO: trigger update of appropriate epic if parentId matches after conversation to redux.
        }

        return TaskDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/tasks/domain/TaskDomain.js